import React, { useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Alert,
  Chip,
  Skeleton,
  CircularProgress,
} from '@mui/material';
import {
  People as PeopleIcon,
  Chat as ChatIcon,
  SmartToy as SmartToyIcon,
  TrendingUp as TrendingUpIcon,
  BarChart as BarChartIcon,
  FileDownload as FileDownloadIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { useAdminDashboard } from '../../hooks/useAdminDashboard';
interface DashboardProps {
  onNavigate: (path: string) => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ onNavigate }) => {
  const { stats, loading, error, fetchStats, clearError } = useAdminDashboard();

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (error) {
    return (
      <Box>
        <Alert severity="error" onClose={clearError} sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const isLoading = loading;

  const StatCard: React.FC<{
    title: string;
    value: number | string;
    icon: React.ReactNode;
    color: string;
    subtitle?: string;
  }> = ({ title, value, icon, color, subtitle }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={1}>
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              backgroundColor: `${color}.100`,
              color: `${color}.600`,
              mr: 2,
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" component="h3" color="text.secondary">
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" fontWeight="bold" mb={1}>
          {isLoading ? (
            <Skeleton variant="text" width={60} height={40} />
          ) : typeof value === 'number' ? (
            value.toLocaleString()
          ) : (
            value
          )}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 600 }}>
        管理者ダッシュボード
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
        システム全体の統計と監視情報
      </Typography>

      {/* システム概要 */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          システム概要
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="総ユーザー数"
              value={stats?.overview?.total_users || 0}
              icon={<PeopleIcon />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="今日のアクティブユーザー"
              value={stats?.overview?.active_users_today || 0}
              icon={<TrendingUpIcon />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="企業数"
              value={stats?.overview?.total_companies || 0}
              icon={<BusinessIcon />}
              color="info"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="承認待ち企業"
              value={stats?.overview?.pending_company_approvals || 0}
              icon={<ScheduleIcon />}
              color="warning"
            />
          </Grid>
        </Grid>
      </Box>

      {/* 学生メトリクス */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          学生メトリクス
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="総学生数"
              value={stats?.user_metrics?.total_students || 0}
              icon={<PeopleIcon />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="アクティブ学生"
              value={stats?.user_metrics?.active_students || 0}
              icon={<CheckCircleIcon />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="新規学生登録"
              value={stats?.user_metrics?.new_student_registrations || 0}
              icon={<TrendingUpIcon />}
              color="info"
              subtitle="今日"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="継続率"
              value={`${stats?.user_metrics?.retention_rate || 0}%`}
              icon={<AssessmentIcon />}
              color="secondary"
            />
          </Grid>
        </Grid>
      </Box>

      {/* 企業メトリクス */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          企業メトリクス
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="総企業数"
              value={stats?.company_metrics?.total_companies || 0}
              icon={<BusinessIcon />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="承認済み企業"
              value={stats?.company_metrics?.approved_companies || 0}
              icon={<CheckCircleIcon />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="承認待ち"
              value={stats?.company_metrics?.pending_approvals || 0}
              icon={<ScheduleIcon />}
              color="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="アクティブ採用担当者"
              value={stats?.company_metrics?.active_recruiters || 0}
              icon={<PeopleIcon />}
              color="info"
            />
          </Grid>
        </Grid>
      </Box>

      {/* エンゲージメントメトリクス */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          エンゲージメントメトリクス
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="総スカウトメッセージ"
              value={stats?.engagement_metrics?.total_scout_messages || 0}
              icon={<ChatIcon />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="スカウト返信率"
              value={`${stats?.engagement_metrics?.scout_response_rate || 0}%`}
              icon={<AssessmentIcon />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="タイムライン投稿"
              value={stats?.engagement_metrics?.timeline_posts || 0}
              icon={<TimelineIcon />}
              color="info"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="今日のチャットセッション"
              value={stats?.engagement_metrics?.chat_sessions_today || 0}
              icon={<ChatIcon />}
              color="secondary"
            />
          </Grid>
        </Grid>
      </Box>

      {/* 管理機能カード */}
      {/*
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        管理機能
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SmartToyIcon
                  sx={{ color: 'primary.main', mr: 2, fontSize: 32 }}
                />
                <Box>
                  <Typography variant="h6">専門家AI管理</Typography>
                  <Typography variant="body2" color="textSecondary">
                    専門家AIの作成・編集・管理
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                新しい専門家AIの作成、既存AIの設定変更、研修資料のアップロードを行えます。
              </Typography>
              <Chip
                label="Gemini風インターフェース"
                size="small"
                color="primary"
                variant="outlined"
              />
            </CardContent>
            <CardActions>
              <Button
                variant="contained"
                onClick={() => onNavigate('/admin/ai-management')}
                startIcon={<SmartToyIcon />}
              >
                管理画面へ
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PeopleIcon
                  sx={{ color: 'secondary.main', mr: 2, fontSize: 32 }}
                />
                <Box>
                  <Typography variant="h6">ユーザー管理</Typography>
                  <Typography variant="body2" color="textSecondary">
                    学生ユーザーの基本情報確認
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                登録ユーザーの一覧確認、権限変更、アカウント状態の管理を行えます。
              </Typography>
              <Chip
                label="権限管理対応"
                size="small"
                color="secondary"
                variant="outlined"
              />
            </CardContent>
            <CardActions>
              <Button
                variant="contained"
                onClick={() => onNavigate('/admin/users')}
                startIcon={<PeopleIcon />}
              >
                ユーザー一覧へ
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BarChartIcon
                  sx={{ color: 'success.main', mr: 2, fontSize: 32 }}
                />
                <Box>
                  <Typography variant="h6">利用統計・分析</Typography>
                  <Typography variant="body2" color="textSecondary">
                    システム利用状況の確認
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                ユーザーの利用状況、AI利用ログ、システム統計の確認ができます。
              </Typography>
              <Chip
                label="リアルタイム更新"
                size="small"
                color="success"
                variant="outlined"
              />
            </CardContent>
            <CardActions>
              <Button
                variant="contained"
                onClick={() => onNavigate('/admin/statistics')}
                startIcon={<BarChartIcon />}
              >
                統計画面へ
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FileDownloadIcon
                  sx={{ color: 'warning.main', mr: 2, fontSize: 32 }}
                />
                <Box>
                  <Typography variant="h6">データエクスポート</Typography>
                  <Typography variant="body2" color="textSecondary">
                    CSV・Excel形式でデータ出力
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                ユーザー一覧、利用統計、AI利用ログをCSV・Excel形式でダウンロードできます。
              </Typography>
              <Chip
                label="CSV/Excel対応"
                size="small"
                color="warning"
                variant="outlined"
              />
            </CardContent>
            <CardActions>
              <Button
                variant="contained"
                onClick={() => onNavigate('/admin/export')}
                startIcon={<FileDownloadIcon />}
              >
                エクスポート画面へ
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>
      */}
    </Box>
  );
};
